import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import {
  certificationDefinitionsApi,
  ENTITY_TYPES,
  type CertificationDefinitionDto,
  type EntityType,
} from "@/api/certifications";
import { CertificationDefinitionsTable } from "./CertificationDefinitionsTable";
import { CertificationDefinitionEditorDialog } from "./CertificationDefinitionEditorDialog";
import { DeactivateConfirmDialog } from "./DeactivateConfirmDialog";

type ActiveFilter = "all" | "active" | "inactive";
type EntityFilter = "all" | EntityType;

const QUERY_KEY = ["admin", "certification-definitions"] as const;

/**
 * Certifications Module v1.0 §4a — admin authoring surface for the
 * cross-entity CertificationDefinition catalog. Reachable at
 * <c>/admin/certifications</c> for users who hold
 * <c>CertificationDefinitions.Read</c>; the route component itself is
 * permission-gated by <see cref="RequireAuth"/>. The
 * <c>FeatureFlags:Certifications</c> dark-ship guard was retired by
 * PR 3 after staging soak — the page is permanent.
 */
export function CertificationsAdminPage() {
  const { hasPermission } = useAuth();
  const canRead = hasPermission("CertificationDefinitions.Read");
  const canCreate = hasPermission("CertificationDefinitions.Create");
  const canUpdate = hasPermission("CertificationDefinitions.Update");
  const canDeactivate = hasPermission("CertificationDefinitions.Deactivate");

  const toast = useToast();
  const qc = useQueryClient();

  const [entityFilter, setEntityFilter] = useState<EntityFilter>("all");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("active");
  const [stageToFilter, setStageToFilter] = useState<string>("");
  const [search, setSearch] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CertificationDefinitionDto | null>(null);
  const [pendingDeactivate, setPendingDeactivate] =
    useState<CertificationDefinitionDto | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  const queryArgs = useMemo(
    () => ({
      entityType: entityFilter === "all" ? undefined : entityFilter,
      active:
        activeFilter === "all"
          ? undefined
          : activeFilter === "active"
            ? true
            : false,
      stageTo: stageToFilter || undefined,
    }),
    [entityFilter, activeFilter, stageToFilter],
  );

  const definitionsQ = useQuery({
    queryKey: [...QUERY_KEY, queryArgs],
    queryFn: () => certificationDefinitionsApi.list(queryArgs),
    enabled: canRead,
  });

  const stagesQ = useQuery({
    queryKey: ["admin", "certification-definitions", "stages", entityFilter],
    queryFn: () =>
      entityFilter === "all"
        ? Promise.resolve([])
        : certificationDefinitionsApi.stages(entityFilter),
    enabled: canRead && entityFilter !== "all",
    staleTime: 5 * 60_000,
  });

  // Reset stage filter when the entity-type filter changes — stage codes
  // are entity-scoped and a stale "Certified" selection from Broker would
  // empty the result set under Loan even if Loan also has a "Certified".
  useEffect(() => {
    setStageToFilter("");
  }, [entityFilter]);

  const refresh = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  // Server doesn't expose label search yet — filter client-side. PR 1's
  // list endpoint returns at most a few hundred rows, so this is fine.
  const visible = useMemo(() => {
    const all = definitionsQ.data ?? [];
    const needle = search.trim().toLowerCase();
    const filtered = needle.length === 0
      ? all
      : all.filter((d) => d.label.toLowerCase().includes(needle));
    return [...filtered].sort((a, b) =>
      a.entityType.localeCompare(b.entityType) ||
      a.stageTo.localeCompare(b.stageTo) ||
      a.sortOrder - b.sortOrder ||
      a.label.localeCompare(b.label),
    );
  }, [definitionsQ.data, search]);

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (def: CertificationDefinitionDto) => {
    setEditing(def);
    setEditorOpen(true);
  };

  const reactivate = async (def: CertificationDefinitionDto) => {
    try {
      await certificationDefinitionsApi.reactivate(def.id);
      toast.success(`Definition '${def.label}' reactivated.`);
      refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not reactivate.";
      toast.error(msg);
    }
  };

  const confirmDeactivate = async () => {
    if (!pendingDeactivate) return;
    setDeactivating(true);
    try {
      await certificationDefinitionsApi.deactivate(pendingDeactivate.id);
      toast.success(`Definition '${pendingDeactivate.label}' deactivated.`);
      setPendingDeactivate(null);
      refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not deactivate.";
      toast.error(msg);
    } finally {
      setDeactivating(false);
    }
  };

  if (!canRead) {
    return (
      <div className="text-small text-status-error">
        You do not have permission to view certification definitions.
      </div>
    );
  }

  const tableError = definitionsQ.isError
    ? definitionsQ.error instanceof Error
      ? definitionsQ.error.message
      : "Could not load definitions."
    : null;

  const listIsEmpty =
    !definitionsQ.isLoading &&
    !definitionsQ.isError &&
    (definitionsQ.data ?? []).length === 0;
  const filtersActive =
    entityFilter !== "all" ||
    activeFilter !== "active" ||
    stageToFilter !== "" ||
    search.trim().length > 0;

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Certification Definitions"
        description="Configure the items that gate stage transitions on each entity type."
        actions={
          canCreate && (
            <Button type="button" onClick={openCreate}>
              <Plus size={16} aria-hidden /> New definition
            </Button>
          )
        }
      />

      <div className="flex flex-col md:flex-row md:items-end gap-space-3 mb-space-4">
        <div className="flex-1 min-w-0">
          <div className="relative">
            <Search
              size={16}
              aria-hidden
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by label"
              className="pl-space-5"
              aria-label="Search definitions by label"
            />
          </div>
        </div>
        <Select
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value as EntityFilter)}
          aria-label="Filter by entity type"
          className="md:w-44"
        >
          <option value="all">All entity types</option>
          {ENTITY_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Select>
        <Select
          value={stageToFilter}
          onChange={(e) => setStageToFilter(e.target.value)}
          aria-label="Filter by stage to"
          disabled={entityFilter === "all" || (stagesQ.data?.length ?? 0) === 0}
          className="md:w-48"
        >
          <option value="">All target stages</option>
          {(stagesQ.data ?? []).map((s) => (
            <option key={s.code} value={s.code}>{s.displayName}</option>
          ))}
        </Select>
        <Select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
          aria-label="Filter by status"
          className="md:w-36"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </Select>
      </div>

      {listIsEmpty && !filtersActive ? (
        <div className="rounded-md border border-dashed border-border-light bg-white-warm p-space-5 text-center">
          <h3 className="font-display font-bold text-h4 text-onyx">
            No certification definitions yet
          </h3>
          <p className="text-small text-gray-mid mt-space-2 max-w-reading mx-auto">
            Definitions configure the self-attestation items that gate stage transitions.
            Each entity type — Broker, Loan, Deal, Vendor, SPV, Investor — can have its own.
          </p>
          {canCreate && (
            <Button className="mt-space-3" type="button" onClick={openCreate}>
              <Plus size={16} aria-hidden /> Create your first definition
            </Button>
          )}
        </div>
      ) : (
        <CertificationDefinitionsTable
          rows={visible}
          loading={definitionsQ.isLoading}
          error={tableError}
          emptyMessage="No definitions match the current filters."
          canUpdate={canUpdate}
          canDeactivate={canDeactivate}
          onEdit={openEdit}
          onDeactivate={(d) => setPendingDeactivate(d)}
          onReactivate={reactivate}
        />
      )}

      <CertificationDefinitionEditorDialog
        open={editorOpen}
        definition={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={refresh}
      />

      <DeactivateConfirmDialog
        open={Boolean(pendingDeactivate)}
        definition={pendingDeactivate}
        busy={deactivating}
        onClose={() => {
          if (!deactivating) setPendingDeactivate(null);
        }}
        onConfirm={confirmDeactivate}
      />
    </>
  );
}
